/* global globalThis */
import {
  selectCitiesCache,
  selectHexes,
  selectPlayers,
  selectRound,
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {calcBfsDistances, getBotGridRenderer} from "../botGrid";
import {getBotStrategy} from "./botStrategyStore";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {NEUTRAL_PLAYER_COLOR} from "../../../constants/players";
import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {
  BOT_BONUS_FRONTIER_CITY_MAX_ENEMY_DISTANCE,
  BOT_BONUS_FRONTIER_FARM_CAP,
  BOT_BONUS_STAGING_FARM_CAP,
  BOT_BONUS_STAGING_SWITCH_MIN_GAIN,
} from "../../../configs/botBonusBehavior";

// Волна 33: «ось удара» бонусного бота (см. configs/botBonusBehavior).
//
// Раз в раунд бот с пакетом «сильных городов» выбирает ближайшего ЖИВОГО
// противника, город-«кузницу» (staging — куда идут отряды, где строится
// кластер казарм/лагерь/арсенал) и тыловой хаб (rear — склад с кольцом ферм).
// Стройка, найм и перемещение читают одну и ту же ось, поэтому армия и
// застройка движутся в одну сторону.
//
// Запись в стратегии ИММУТАБЕЛЬНА: ensureBonusAxis всегда присваивает новый
// объект и никогда не правит старый, поэтому снимок стратегий песочницы
// сильного режима (botStrategyStore.cloneStrategyValue) может делить ее по
// ссылке без копирования карт дистанций.

// Насколько новый ближайший противник должен быть ближе прежнего, чтобы ось
// переключилась на него (гистерезис: цель не мигает между раундами)
const ENEMY_SWITCH_MIN_GAIN = 2;

/**
 * Живой противник — не сам бот, не нейтрал и хотя бы с одним городом: игрока
 * без городов захватывать негде, его отряды учитывает обычная логика боя.
 */
export const isLiveEnemy = (player, currentPlayer, citiesCache) => (
  Boolean(player)
  && player.id !== currentPlayer.id
  && player.color !== NEUTRAL_PLAYER_COLOR
  && (citiesCache?.[player.color] || []).length > 0
);

const collectQuarters = (cities) => cities.flatMap((city) => getAllQuartersOfCity(city));

/** Минимальная дистанция по карте BFS среди кварталов города; Infinity — недостижимо */
export const calcCityMinDistance = (city, distances) => {
  let best = Infinity;
  for (const quarterKey of getAllQuartersOfCity(city)) {
    const distance = distances.get(quarterKey);
    if (distance !== undefined && distance < best) {
      best = distance;
    }
  }
  return best;
};

const countCityToken = (city, token) => (city[token] || []).length;

const hasForgeBuildings = (city) => (
  countCityToken(city, COMMON_BUILDING_TOKENS.BARRACKS) > 0
  || countCityToken(city, SPECIAL_BUILDING_TOKENS.MILITARY_CAMP) > 0
  || countCityToken(city, SPECIAL_BUILDING_TOKENS.ARSENAL) > 0
);

/**
 * Класс своего города для правил волны 33.
 *  - тыловой хаб (rearCityKey) — всегда 'rear': тыл существует, даже когда все
 *    города в зоне противника;
 *  - кузница (stagingCityKey) — всегда 'frontier';
 *  - остальные — по BFS от кварталов живых противников (нейтралы не в счет).
 * Принимает и синтетический город {centralQuarter, quarter: []} для ЦК,
 * запланированного в текущей цепочке строительства.
 * distances — карта BFS от кварталов живых противников (по умолчанию из оси;
 * стройка передает свою realEnemyQuarterDistances).
 */
export const classifyOwnCity = (city, axis, distances) => {
  if (!city || !axis) {
    return 'rear';
  }
  const distanceMap = distances || axis.enemyQuarterDistances;
  if (city.centralQuarter === axis.rearCityKey) {
    return 'rear';
  }
  if (city.centralQuarter === axis.stagingCityKey) {
    return 'frontier';
  }
  return calcCityMinDistance(city, distanceMap) <= BOT_BONUS_FRONTIER_CITY_MAX_ENEMY_DISTANCE
    ? 'frontier'
    : 'rear';
};

/**
 * Потолок ферм города по его классу: тыл без потолка, фронтир — одна ферма
 * (ретранслятор снабжения), кузница — две (там собирается армия).
 */
export const getFrontierFarmCap = (cityClass, isStaging) => {
  if (cityClass !== 'frontier') {
    return Infinity;
  }
  // Калибровочные оверрайды харнесса (SIM_W33_STAGING_FARM_CAP /
  // SIM_W33_FRONTIER_FARM_CAP); в проде не задаются
  if (isStaging) {
    return typeof globalThis.__bonusStagingFarmCap === 'number'
      ? globalThis.__bonusStagingFarmCap
      : BOT_BONUS_STAGING_FARM_CAP;
  }
  return typeof globalThis.__bonusFrontierFarmCap === 'number'
    ? globalThis.__bonusFrontierFarmCap
    : BOT_BONUS_FRONTIER_FARM_CAP;
};

const pickBest = (items, scoreOf) => {
  let best = null;
  let bestScore = null;
  for (const item of items) {
    const score = scoreOf(item);
    if (best === null || score > bestScore) {
      best = item;
      bestScore = score;
    }
  }
  return best;
};

/**
 * Ось удара на текущий раунд: {round, enemyColor, enemyPlayerId,
 * stagingCityKey, rearCityKey, enemyQuarterDistances (Map, от кварталов ВСЕХ
 * живых противников), enemyColorQuarterDistances (Map, от кварталов
 * enemyColor), stagingSwitches}.
 *
 * null — пакет выключен (нет strongCities), у бота нет городов или нет живых
 * противников: правила волны 33 тогда не действуют. Кэш на раунд; пустая ось
 * пересчитывается в том же раунде, как только у бота появляются города.
 */
export const ensureBonusAxis = (state, player, bonusProfile) => {
  if (!bonusProfile || !bonusProfile.strongCities) {
    return null;
  }
  const strategy = getBotStrategy(player.id);
  const round = selectRound(state);
  const citiesCache = selectCitiesCache(state);
  const ownCities = citiesCache?.[player.color] || [];

  const cached = strategy.bonusAxis;
  if (strategy.bonusAxisRound === round && (cached || ownCities.length === 0)) {
    return cached || null;
  }

  const players = selectPlayers(state);
  const liveEnemies = players.filter((otherPlayer) => isLiveEnemy(otherPlayer, player, citiesCache));
  if (ownCities.length === 0 || liveEnemies.length === 0) {
    strategy.bonusAxis = null;
    strategy.bonusAxisRound = round;
    return null;
  }

  const hexes = selectHexes(state);
  const gridRenderer = getBotGridRenderer(state);

  const ownQuarterDistances = calcBfsDistances(collectQuarters(ownCities), hexes, gridRenderer);
  const enemyDistanceByColor = new Map();
  liveEnemies.forEach((enemy) => {
    const quarters = collectQuarters(citiesCache[enemy.color]);
    let best = Infinity;
    quarters.forEach((quarterKey) => {
      const distance = ownQuarterDistances.get(quarterKey);
      if (distance !== undefined && distance < best) {
        best = distance;
      }
    });
    enemyDistanceByColor.set(enemy.color, best);
  });

  // Ближайший противник с гистерезисом: прежний остается, пока жив и новый
  // кандидат не ближе его на ENEMY_SWITCH_MIN_GAIN
  let enemy = pickBest(liveEnemies, (candidate) => -enemyDistanceByColor.get(candidate.color));
  const previousEnemy = cached && liveEnemies.find((candidate) => candidate.color === cached.enemyColor);
  if (previousEnemy && previousEnemy !== enemy) {
    const previousDistance = enemyDistanceByColor.get(previousEnemy.color);
    const bestDistance = enemyDistanceByColor.get(enemy.color);
    if (!(bestDistance <= previousDistance - ENEMY_SWITCH_MIN_GAIN)) {
      enemy = previousEnemy;
    }
  }

  const enemyQuarterDistances = calcBfsDistances(
    liveEnemies.flatMap((candidate) => collectQuarters(citiesCache[candidate.color])), hexes, gridRenderer
  );
  const enemyColorQuarterDistances = calcBfsDistances(
    collectQuarters(citiesCache[enemy.color]), hexes, gridRenderer
  );

  // Тыловой хаб — самый дальний от всех живых противников свой город
  // (равенство: больше ферм, затем стартовый город). В R1 ось считается из
  // executeBotStep раньше, чем стройка запомнит strategy.homeHexKey, — дом
  // тогда тот же, что она выберет: первый город из кэша
  const homeHexKey = strategy.homeHexKey || ownCities[0].centralQuarter;
  const rearCity = pickBest(ownCities, (city) => (
    Math.min(calcCityMinDistance(city, enemyQuarterDistances), 10_000) * 1000
    + countCityToken(city, COMMON_BUILDING_TOKENS.FARM) * 10
    + (city.centralQuarter === homeHexKey ? 1 : 0)
  ));

  // Кузница — ближайший к enemyColor свой город, кроме тыла; при одном городе
  // она же тыл. Гистерезис: прежняя кузница держится, пока она своя, не тыл и
  // уже застроена под армию; переезд — только к городу заметно ближе к
  // противнику, где казарма уже есть
  let stagingSwitches = cached ? cached.stagingSwitches : 0;
  let stagingCity;
  if (ownCities.length === 1) {
    stagingCity = ownCities[0];
  } else {
    const candidates = ownCities.filter((city) => city !== rearCity);
    const stagingDistance = (city) => calcCityMinDistance(city, enemyColorQuarterDistances);
    const bestCandidate = pickBest(candidates, (city) => (
      -Math.min(stagingDistance(city), 10_000) * 1000
      + countCityToken(city, COMMON_BUILDING_TOKENS.BARRACKS) * 10
      + (city.centralQuarter === cached?.stagingCityKey ? 1 : 0)
    ));
    const previousStaging = cached
      && candidates.find((city) => city.centralQuarter === cached.stagingCityKey);
    if (previousStaging && previousStaging !== bestCandidate && hasForgeBuildings(previousStaging)) {
      const isFarBetter = stagingDistance(bestCandidate)
        <= stagingDistance(previousStaging) - BOT_BONUS_STAGING_SWITCH_MIN_GAIN;
      stagingCity = isFarBetter && countCityToken(bestCandidate, COMMON_BUILDING_TOKENS.BARRACKS) > 0
        ? bestCandidate
        : previousStaging;
    } else {
      stagingCity = bestCandidate;
    }
    if (cached && cached.stagingCityKey && cached.stagingCityKey !== stagingCity.centralQuarter) {
      stagingSwitches += 1;
    }
  }

  const axis = {
    round,
    enemyColor: enemy.color,
    enemyPlayerId: enemy.id,
    stagingCityKey: stagingCity.centralQuarter,
    rearCityKey: rearCity.centralQuarter,
    enemyQuarterDistances,
    enemyColorQuarterDistances,
    stagingSwitches,
  };
  strategy.bonusAxis = axis;
  strategy.bonusAxisRound = round;
  return axis;
};

/** Ось текущего раунда без пересчета (для решений, у которых нет профиля под рукой) */
export const getBonusAxis = (playerId) => getBotStrategy(playerId).bonusAxis || null;
